import React, { useState } from "react";
import { Subscribe } from "unstated";
import { t } from "@lingui/macro";
import { i18n, Card } from "idc-ui-react";
// Fluent IU
import {
  Stack,
  Dialog,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Checkbox,
  Icon,
  DialogType,
  DefaultPalette,
  Text,
  Separator,
  Overlay,
  Spinner
} from "@fluentui/react";
// Our modules
import { store } from "../../stores/Main";
import { Section } from "../../components";
import MfaModal from "./MfaModal";
import Herocard from "./Herocard";

const AccountPage = ({ store }) => {
  const { state, toggleITAccount, notifications, urls } = store;
  const { loggedInUser } = state;

  const [hideItAccountDialog, setHideItAccountDialog] = useState(true);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [showMfaDialog, setShowMfaDialog] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  async function handleItAccountToggle() {
    setShowSpinner(true);
    const res = await toggleITAccount();
    if (res.status === 200) {
      window.location.href = urls.logout;
    } else {
      notifications.setError(() =>
        i18n._(
          t`There was an error disabling the it account. Please contact your system administrator if the problem persist.`
        )
      );
    }
  }

  const cards = [
    {
      title: i18n._(t`Disable IT account`),
      description: i18n._(
        t`Here you can deactivate your IT account. Note that you cannot reactivate it yourself. To have the account reactivated, contact your user administrator.`
      ),
      options: [i18n._(t`Disable IT account`)],
      iconProps: { iconName: "blockedSite" },
      clickOnCard: () => setHideItAccountDialog(false)
    },
    {
      title: i18n._(t`Change Multi-Factor Authentication`),
      description: i18n._(
        t`Here you can change the type of two-step verification you use to log in to ID Connect.`
      ),
      options: [i18n._(t`Change Multi-Factor Authentication`)],
      iconProps: { iconName: "settingssecure" },
      clickOnCard: () => setShowMfaDialog(true)
    }
  ];

  return (
    <Section hero returnLink text={i18n._(t`Manage your account`)}>
      <MfaModal
        isOpen={showMfaDialog}
        setIsOpen={setShowMfaDialog}
        store={store}
      />
      <Dialog
        hidden={hideItAccountDialog}
        dialogContentProps={{
          type: DialogType.normal,
          showCloseButton: false,
          title: (
            <Stack horizontal verticalAlign="center">
              <Icon
                iconName="blockedSite"
                styles={{
                  root: {
                    paddingRight: "10px",
                    color: DefaultPalette.blue,
                    fontSize: "28px"
                  }
                }}
              />
              {i18n._(t`Disable IT account`)}
            </Stack>
          )
        }}
      >
        <Stack>
          <Text>
            {i18n._(
              t`Note that this can only be undone by your user administrator.`
            )}
          </Text>
          <Separator />
          <Checkbox
            label={i18n._(t`Do you understand the consequences`)}
            onChange={(ev, checked) => setIsCheckboxChecked(checked)}
            defaultChecked={false}
          />
          {showSpinner && (
            <Overlay
              styles={{
                root: {
                  backgroundColor: "rgba(255, 255, 255, 0.8)"
                }
              }}
            >
              <Stack
                horizontalAlign="center"
                verticalAlign="center"
                style={{ height: "100%" }}
              >
                <Spinner />
              </Stack>
            </Overlay>
          )}
        </Stack>
        <DialogFooter>
          <DefaultButton
            onClick={() => setHideItAccountDialog(true)}
            disabled={showSpinner}
          >
            {i18n._(t`Cancel`)}
          </DefaultButton>
          <PrimaryButton
            onClick={handleItAccountToggle}
            disabled={!isCheckboxChecked || showSpinner}
          >
            {i18n._(t`Disable`)}
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
      <Stack
        styles={{
          root: { display: "flex", flexDirection: "row" }
        }}
        tokens={{ childrenGap: 20 }}
        horizontal
      >
        <Herocard loggedInUser={loggedInUser} urls={store.urls} />
        <Stack
          styles={{
            root: {
              display: "grid",
              marginTop: "20px",
              gridTemplateColumns: "repeat(3, 1fr)",
              gridGap: "20px"
            }
          }}
        >
          {cards.map(card => (
            <Card key={card.title} {...card} />
          ))}
        </Stack>
      </Stack>
    </Section>
  );
};

const StoreWrapper = props => (
  <Subscribe to={[store]}>
    {store => <AccountPage {...props} store={store} />}
  </Subscribe>
);

export default StoreWrapper;
